import React, {useContext, useState} from 'react';
import {ContextCart} from "@contexts/ContextCartProvider";
import {ContextTaxonomy} from "@contexts/ContextTaxonomyProvider";
import {ContextOperatorTask} from "@contexts/ContextOperatorTaskProvider";
import {useGetPriceId} from "@components/base/Price/Price";
import Portal from "@components/base/Portal/Portal";
import {
	RedirectTo,
	NavigateToTab,
	RedirectToHostingFromHomePageBanner,
	AddToCart,
	StartShopikoTrial,
	StartWebnodeTrial,
	StartSiteBuilderTrial,
	ScrollToTarget,
	SubmitOperatorTask
} from "@helpers/HelperFunc";

const ActionRegistry = {
	RedirectTo,
	NavigateToTab,
	RedirectToHostingFromHomePageBanner,
	AddToCart,
	StartShopikoTrial,
	StartWebnodeTrial,
	StartSiteBuilderTrial,
	ScrollToTarget,
	SubmitOperatorTask
};

/**
 * Resolves a single parameter.
 */
const resolveParam = (param, availableParams) => {
	if (typeof param !== "string") {
		return param;
	}

	const match = param.match(/^#(.+)#$/);
	if (!match) {
		return param;  // If not #variable# format - return original
	}

	const varName = match[1];
	return Object.prototype.hasOwnProperty.call(availableParams, varName)
	  ? availableParams[varName]
	  : null;  // Variable with # but not exists in availableParams - return null
};

/**
 * Resolves an array of parameters.
 */
const resolveParams = (params = [], availableParams = {}) => {
	if (!Array.isArray(params)) {
		return [];
	}

	return params.map((p) => resolveParam(p, availableParams));
};

/**
 * Resolves an object of parameters (for modal props).
 */
const resolveObjectParams = (params = {}, availableParams = {}) => {
	if (!params || typeof params !== 'object' || Array.isArray(params)) {
		return {};
	}

	const resolved = {};
	for (const [key, value] of Object.entries(params)) {
		resolved[key] = resolveParam(value, availableParams);
	}
	return resolved;
};

/**
 * Hook for run actions from data file.
 * @param {Object} customParams - additional params to merge with default ones
 */
const useDataFileAction = (customParams = {}) => {
	const contextCart = useContext(ContextCart);
	const contextTaxonomy = useContext(ContextTaxonomy || null);
	const contextOperatorTask = useContext(ContextOperatorTask || null);
	const getPriceId = useGetPriceId();

	const [activeModalComponent, setActiveModalComponent] = useState(null);

	const availableParams = {
		contextCart,
		contextTaxonomy,
		contextOperatorTask,
		getPriceId,
		...customParams
	};

	const executeAction = (data, runtimeParams = {}) => {
		const Modal = data.ButtonModal ?? data.LinkModal ?? data.ActionModal ?? data.VideoModal ?? data.Modal ?? null;
		const action = data.buttonAction ?? data.linkAction ?? data.action ?? null;
		const params = data.buttonParams ?? data.linkParams ?? data.actionParams ?? data.params ?? null;
		const modalParams = data.buttonModalParams ?? data.linkModalParams ?? data.modalParams ?? null;

		// Merge availableParams with runtimeParams (runtime overrides)
		const mergedParams = {...availableParams, ...runtimeParams};

		if (Modal) {
			const resolvedModalParams = resolveObjectParams(modalParams, mergedParams);
			// Auto-inject brand color so it reaches the modal (and its TextBox components).
			// Data file modalParams (resolvedModalParams) override it if explicitly set.
			const injectedProps = mergedParams.color !== undefined ? {color: mergedParams.color} : {};
			setActiveModalComponent(<Modal key={Date.now()} {...injectedProps} {...resolvedModalParams}/>);
			return true;
		}

		if (action && typeof ActionRegistry[action] === "function") {
			const finalParams = resolveParams(params, mergedParams);
			ActionRegistry[action](...finalParams);
			return true;
		}

		console.log("No action defined.");
		return false;
	};

	const ModalPortal = (
	  <Portal containerId={'modalContainer'}>
		  {activeModalComponent}
	  </Portal>
	);

	return {executeAction, ModalPortal};
};

export default useDataFileAction;